import type { Screen } from '../../types';
import { LogoMark, NavIcon } from '../primitives';

interface NavItem {
  id: Screen;
  label: string;
  icon: string;
  scope?: 'private' | 'company' | 'mixed';
}

const NAV_ITEMS: NavItem[] = [
  { id: 'sources', label: 'Sources', icon: 'plug',  scope: 'company' },
  { id: 'stream',  label: 'Data',    icon: 'inbox', scope: 'company' },
  { id: 'ask',     label: 'Ask',     icon: 'spark', scope: 'mixed'   },
];

interface Props {
  current: Screen;
  onNav: (screen: Screen) => void;
}

export function Sidebar({ current, onNav }: Props) {
  return (
    <aside
      className="sidebar"
      style={{
        width: 76,
        background: 'var(--white)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 0',
        gap: 18,
        flexShrink: 0,
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div style={{ marginBottom: 4 }}>
        <LogoMark size={42} />
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
        {NAV_ITEMS.map((item) => {
          const active = current === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNav(item.id)}
              title={item.label}
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                border: 'none',
                background: active ? 'var(--deep)' : 'transparent',
                color: active ? 'white' : 'var(--slate)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'background .15s, color .15s',
              }}
            >
              <NavIcon name={item.icon} size={20} />
              {item.scope === 'private' && (
                <span style={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: 3, background: 'var(--priv-private)' }} />
              )}
              {item.scope === 'company' && (
                <span style={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: 3, background: 'var(--priv-company)' }} />
              )}
            </button>
          );
        })}

        <div style={{ height: 1, width: 22, background: 'var(--line)', margin: '6px auto' }} />
      </div>

      {/* Add new / plus action */}
      <button
        title="Add source"
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          border: 'none',
          background: 'var(--gradient-brand)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px -4px rgba(45,0,247,.4)',
        }}
      >
        <NavIcon name="plus" size={20} />
      </button>

      <a
        href="https://fairmint.cloudflareaccess.com/cdn-cgi/access/logout"
        title="Sign out"
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          border: 'none',
          background: 'transparent',
          color: 'var(--slate)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          marginTop: 4,
          transition: 'background .15s, color .15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.background = 'var(--surface-2)';
          (e.currentTarget as HTMLAnchorElement).style.color = 'var(--deep)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
          (e.currentTarget as HTMLAnchorElement).style.color = 'var(--slate)';
        }}
      >
        <NavIcon name="logout" size={18} />
      </a>
    </aside>
  );
}
